// My bookings — a partner's booking history across smilinghouse.com,
// BookingPal and the extranet, read from the hub (GET /local/partner/bookings,
// scoped to the partner's own session). Tom, 3 Nov 2026: partners see the
// guest's email and phone, and the NET they receive — never the guest total.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FiCalendar, FiAlertTriangle } from "react-icons/fi";
import { IoIosSearch, IoMdClose } from "react-icons/io";

import Layout from "../../components/Layout/index.js";
import { readErrorMessage } from "../../Util/readError.js";
import { retryRead } from "../../Util/retryRead.js";
import { ShubURL, ShubAuth } from "../../core";
import "../PartnersListView/PartnersListView.scss";
import "../Reservations/Reservations.scss";

// ShubAuth is the placeholder the hub-session transport (Util/hubSession.js)
// swaps for this partner's own session, so the hub answers with their bookings.
const hub = axios.create({ baseURL: ShubURL, headers: { Authorization: `Bearer ${ShubAuth}` } });

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
  { key: "cancelled", label: "Cancelled" },
];
const PAGE_SIZE = 20;

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "-");
const money = (net) =>
  net && net.amount != null
    ? `${net.currency || ""} ${Number(net.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim()
    : "-";
const pill = (status) => (status === "confirmed" ? "approved" : status === "cancelled" ? "declined" : "pending");

const PartnerBookings = (props) => {
  const { token, agency, agent, screenSize, activeMenu, handleToggleMenu, setActiveMenu } = props;
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("upcoming");
  const [page, setPage] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    retryRead(() => hub.get("/local/partner/bookings").then((r) => r.data))
      .then((data) => {
        if (!data || data.success !== true) throw new Error((data && data.error) || "bookings lookup failed");
        setRows(Array.isArray(data.bookings) ? data.bookings : []);
        setLoadError(null);
      })
      .catch((e) => {
        const status = e && e.response && e.response.status;
        console.error("partner bookings load failed", status || "", (e && e.message) || e);
        setRows([]);
        setLoadError(readErrorMessage(status, "your bookings", "the Villa Tracker hub"));
      })
      .finally(() => setIsLoading(false));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "cancelled" && r.status !== "cancelled") return false;
      if (filter === "upcoming" && (r.status === "cancelled" || !r.checkOut || r.checkOut < today)) return false;
      if (filter === "past" && (r.status === "cancelled" || !r.checkOut || r.checkOut >= today)) return false;
      if (!q) return true;
      return [r.reference, r.listingTitle, r.listingId, r.guest && r.guest.name, r.guest && r.guest.email, r.channel]
        .some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [rows, filter, search, today]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const totalNet = useMemo(() => {
    const byCcy = {};
    for (const r of filtered) if (r.status !== "cancelled" && r.net && r.net.amount != null) byCcy[r.net.currency || "?"] = (byCcy[r.net.currency || "?"] || 0) + Number(r.net.amount);
    return Object.entries(byCcy).map(([c, v]) => `${c} ${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}`).join(" · ");
  }, [filtered]);

  return (
    <Layout pageTitle="My bookings" agency={agency} agent={agent} token={token} screenSize={screenSize} activeMenu={activeMenu} handleToggleMenu={handleToggleMenu} setActiveMenu={setActiveMenu}>
      <div className="partners-view reservations-view">
        <div className="page-hero">
          <div className="page-hero-left">
            <div className="page-hero-icon"><FiCalendar size={22} /></div>
            <div>
              <h1 className="page-hero-title">My bookings</h1>
              <p className="page-hero-subhead">Every booking of your villas through Villa Tracker — the guest's contact and what you receive for the stay</p>
            </div>
          </div>
          <div className="page-hero-meta">
            <span className="page-hero-pill">{filtered.length} booking{filtered.length === 1 ? "" : "s"}{totalNet ? ` · ${totalNet} to you` : ""}</span>
            <div className="page-hero-pager" role="group" aria-label="Pagination">
              <button type="button" className="page-hero-pager-btn" onClick={() => setPage(Math.max(0, safePage - 1))} disabled={safePage <= 0} aria-label="Previous page">‹</button>
              <span className="page-hero-pager-label">{safePage + 1} / {pages}</span>
              <button type="button" className="page-hero-pager-btn" onClick={() => setPage(Math.min(pages - 1, safePage + 1))} disabled={safePage >= pages - 1} aria-label="Next page">›</button>
            </div>
          </div>
        </div>

        <div className="partners-toolbar">
          <div className="search-wrap">
            <IoIosSearch className="search-icon" />
            <input className="search-input" value={search} placeholder="Search by reference, villa, guest…" onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
            {search ? <button type="button" className="search-clear" onClick={() => setSearch("")} aria-label="Clear search"><IoMdClose /></button> : null}
          </div>
          <div className="sort-chips" role="tablist" aria-label="Filter bookings">
            {STATUS_FILTERS.map((opt) => (
              <button key={opt.key || "all"} type="button" role="tab" aria-selected={filter === opt.key} className={filter === opt.key ? "active" : ""} onClick={() => { setFilter(opt.key); setPage(0); }}>{opt.label}</button>
            ))}
          </div>
        </div>

        {loadError ? (
          <div className="empty-state"><FiAlertTriangle size={20} /><p>{loadError}</p></div>
        ) : null}

        <div className="partners-grid">
          {isLoading && [0, 1, 2].map((i) => (
            <div className="partner-card res-card skeleton" key={`sk-${i}`}>
              <span className="skeleton-block" style={{ width: 120, height: 18 }} />
              <span className="skeleton-block" style={{ width: 180, height: 12, marginTop: 8 }} />
            </div>
          ))}
          {!isLoading && !loadError && pageRows.length === 0 ? (
            <div className="empty-state"><p>{rows.length ? "No bookings match this filter." : "No bookings yet. Your first one appears here the moment a guest books."}</p></div>
          ) : null}
          {!isLoading && pageRows.map((r) => (
            <div className="partner-card res-card" key={r.id}>
              <div className="partner-card-header">
                <span className="partner-card-serial">{r.reference || "—"}</span>
                <span className={`status-pill res-status ${pill(r.status)}`}>{r.status}</span>
              </div>
              <h3 className="partner-card-name" title={r.listingTitle || r.listingId}>{r.listingTitle || r.listingId || "—"}</h3>
              <div className="partner-card-accountid">{r.channel}{r.channelReservationId ? ` · ${r.channelReservationId}` : ""}</div>
              <div className="res-card-grid">
                <div className="res-card-cell"><span className="res-card-k">Check-in</span><span className="res-card-v">{fmtDate(r.checkIn)}</span></div>
                <div className="res-card-cell"><span className="res-card-k">Check-out</span><span className="res-card-v">{fmtDate(r.checkOut)}</span></div>
                <div className="res-card-cell"><span className="res-card-k">Nights</span><span className="res-card-v">{r.nights != null ? r.nights : "-"}</span></div>
                <div className="res-card-cell"><span className="res-card-k">Guests</span><span className="res-card-v">{r.guests != null ? r.guests : "-"}</span></div>
                <div className="res-card-cell"><span className="res-card-k">Guest</span><span className="res-card-v">{(r.guest && r.guest.name) || "-"}</span></div>
                <div className="res-card-cell"><span className="res-card-k">Email</span><span className="res-card-v">{r.guest && r.guest.email ? <a href={`mailto:${r.guest.email}`}>{r.guest.email}</a> : "-"}</span></div>
                <div className="res-card-cell"><span className="res-card-k">Phone</span><span className="res-card-v">{r.guest && r.guest.phone ? <a href={`tel:${r.guest.phone.replace(/\s+/g, "")}`}>{r.guest.phone}</a> : "-"}</span></div>
                <div className="res-card-cell"><span className="res-card-k">You receive</span><span className="res-card-v">{money(r.net)}</span></div>
                <div className="res-card-cell"><span className="res-card-k">Booked</span><span className="res-card-v">{fmtDate(r.bookedAt)}</span></div>
                {r.cancelledAt ? <div className="res-card-cell"><span className="res-card-k">Cancelled</span><span className="res-card-v">{fmtDate(r.cancelledAt)}</span></div> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default PartnerBookings;
